/**
 * Generic one-per-view image carousel.
 * Markup: <zu-image-carousel data-autoplay="ms">
 *           <div data-viewport><div class="track"><div data-slide>…</div>…</div></div>
 *           <div data-dots></div>
 *         </zu-image-carousel>
 * Auto-slides (0 = off), pauses on hover/focus/touch, supports swipe + dots.
 */
class ZuImageCarousel extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('[data-viewport]');
    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    this.dotsWrap = this.querySelector('[data-dots]');
    this.interval = parseInt(this.dataset.autoplay, 10) || 0;
    this.index = 0;
    this.timer = null;

    if (!this.viewport || this.slides.length <= 1) return;

    this.buildDots();
    this.bind();
    this.start();
  }

  disconnectedCallback() {
    this.stop();
  }

  buildDots() {
    if (!this.dotsWrap) return;
    this.dotsWrap.innerHTML = '';
    this.slides.forEach((_, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'zu-about__dot';
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-label', `Go to image ${i + 1}`);
      dot.addEventListener('click', () => this.goTo(i, true));
      this.dotsWrap.appendChild(dot);
    });
    this.dots = Array.from(this.dotsWrap.children);
    this.setActive(0);
  }

  goTo(i, fromUser) {
    this.index = (i + this.slides.length) % this.slides.length;
    this.viewport.scrollTo({ left: this.index * this.viewport.clientWidth, behavior: 'smooth' });
    this.setActive(this.index);
    if (fromUser) this.restart();
  }

  setActive(i) {
    if (!this.dots) return;
    this.dots.forEach((d, n) => d.classList.toggle('zu-about__dot--active', n === i));
  }

  bind() {
    let raf;
    this.viewport.addEventListener('scroll', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const i = Math.round(this.viewport.scrollLeft / this.viewport.clientWidth);
        if (i !== this.index) {
          this.index = i;
          this.setActive(i);
        }
      });
    });

    ['mouseenter', 'focusin', 'touchstart'].forEach((e) =>
      this.addEventListener(e, () => this.stop(), { passive: true })
    );
    ['mouseleave', 'focusout'].forEach((e) => this.addEventListener(e, () => this.start()));

    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        this.viewport.scrollTo({ left: this.index * this.viewport.clientWidth });
      }, 150);
    });
  }

  start() {
    if (!this.interval || this.timer) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.timer = setInterval(() => this.goTo(this.index + 1, false), this.interval);
  }

  stop() {
    clearInterval(this.timer);
    this.timer = null;
  }

  restart() {
    this.stop();
    this.start();
  }
}

if (!customElements.get('zu-image-carousel')) {
  customElements.define('zu-image-carousel', ZuImageCarousel);
}
